import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const fmtDMY = (d) => { try { const x = new Date(d); return `${String(x.getDate()).padStart(2,'0')}/${String(x.getMonth()+1).padStart(2,'0')}/${x.getFullYear()}`; } catch { return d; } };

export const CreateManualTimesheetDialog = ({ shifts, timesheets, staffUsers, token, apiCall, onDone }) => {
  const [open, setOpen] = useState(false);
  const [shiftId, setShiftId] = useState('');
  const [form, setForm] = useState({ start_time: '', end_time: '', break_minutes: 0, approver_name: '', approver_designation: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const eligibleShifts = useMemo(() => {
    const tsShiftIds = new Set((timesheets || []).map((t) => t.shift_id));
    return (shifts || [])
      .filter((s) => s.assigned_to && !tsShiftIds.has(s.id) && new Date(`${s.date}T${s.start_time || '00:00'}`) <= new Date())
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [shifts, timesheets]);

  const staffName = (uid) => (staffUsers || []).find((s) => s.user.id === uid)?.user.name || 'Staff';

  const pickShift = (id) => {
    setShiftId(id);
    const s = eligibleShifts.find((x) => x.id === id);
    if (s) setForm((f) => ({ ...f, start_time: s.start_time, end_time: s.end_time }));
  };

  const submit = async () => {
    if (!shiftId) { toast.error('Select a shift'); return; }
    if (!form.approver_name.trim() || !form.approver_designation.trim()) {
      toast.error('Client approver name and designation are required');
      return;
    }
    setBusy(true);
    try {
      await apiCall('/admin/timesheets/manual', 'POST', { shift_id: shiftId, ...form }, token);
      toast.success('Unsigned timesheet created and approved — approver recorded');
      setOpen(false);
      setShiftId('');
      onDone();
    } catch (e) {
      toast.error(e.detail || 'Failed to create timesheet');
    }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50"
        onClick={() => setOpen(true)} data-testid="create-manual-timesheet-btn">
        + Create Unsigned Timesheet
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create timesheet without signature</DialogTitle>
            <DialogDescription>
              For shifts where the staff member forgot to get the timesheet signed. Record who at the client
              authorised the hours (e.g. by phone/email). It will be created pre-approved and clearly flagged
              "Approved without signature".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Completed shift (no timesheet yet) *</Label>
              <select value={shiftId} onChange={(e) => pickShift(e.target.value)} data-testid="manual-ts-shift-select"
                className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white">
                <option value="">Select shift…</option>
                {eligibleShifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {fmtDMY(s.date)} — {s.title || s.location} — {staffName(s.assigned_to)} ({s.start_time}-{s.end_time})
                  </option>
                ))}
              </select>
              {eligibleShifts.length === 0 && <p className="text-xs text-slate-500 mt-1">No past assigned shifts without a timesheet.</p>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Start</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="manual-ts-start" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="manual-ts-end" />
              </div>
              <div>
                <Label>Break (min)</Label>
                <Input type="number" min="0" value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: parseInt(e.target.value) || 0 })} data-testid="manual-ts-break" />
              </div>
            </div>
            <div>
              <Label>Client approver name *</Label>
              <Input value={form.approver_name} onChange={(e) => setForm({ ...form, approver_name: e.target.value })} placeholder="e.g. Sarah Jones" data-testid="manual-ts-approver-name" />
            </div>
            <div>
              <Label>Designation *</Label>
              <Input value={form.approver_designation} onChange={(e) => setForm({ ...form, approver_designation: e.target.value })} placeholder="e.g. Home Manager" data-testid="manual-ts-approver-designation" />
            </div>
            <div>
              <Label>Notes (how approval was obtained)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Hours confirmed by phone with the home manager" data-testid="manual-ts-notes" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={submit} data-testid="manual-ts-submit">
                Create & approve without signature
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
